import type { ReactNode } from 'react'
import type { StockRow } from '@/lib/market/types'

import Link from 'next/link'
import { useMemo } from 'react'
import { ChangeText } from '@/components/market/change'
import { useQuotes } from '@/components/market/live-provider'
import { formatKes, formatMultiple, formatPercent, formatPrice } from '@/lib/market/format'

const PeersTable = ({ peers }: { peers: StockRow[] }): ReactNode => {
    const specs = useMemo(() => peers.map(peer => peer.live), [peers])
    const quotes = useQuotes(specs)

    if (!peers.length) {
        return <p className='text-sm text-muted-foreground'>This company is the only listing in its sector.</p>
    }

    const rows = peers
        .map((peer, index) => ({ peer, quote: quotes[index], cap: quotes[index].last * peer.shares }))
        .sort((a, b) => b.cap - a.cap)

    return (
        <div className='-mx-4 overflow-x-auto px-4 scroll-thin sm:mx-0 sm:px-0'>
            <table className='w-full min-w-[620px] text-left text-[13px]'>
                <caption className='sr-only'>Sector peers</caption>
                <thead>
                    <tr className='border-b border-border text-[11px] uppercase tracking-[0.06em] text-muted-foreground'>
                        <th scope='col' className='py-2 pr-3 font-medium'>Share</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>Last</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>Today</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>Mkt cap</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>P/E</th>
                        <th scope='col' className='py-2 pr-3 text-right font-medium'>Yield</th>
                        <th scope='col' className='py-2 text-right font-medium'>12 months</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(({ peer, quote, cap }) => (
                        <tr key={peer.id} className='border-b border-border/60 last:border-0 hover:bg-accent/40'>
                            <td className='max-w-56 py-2 pr-3'>
                                <Link href={`/stocks/${peer.slug}`} className='group block'>
                                    <span className='font-mono text-[13px] font-semibold text-foreground group-hover:text-primary'>{peer.ticker}</span>
                                    <span className='block truncate text-xs text-muted-foreground'>
                                        <span data-mdlfy-cms={`companies:${peer.id}:name`}>{peer.name}</span>
                                    </span>
                                </Link>
                            </td>
                            <td className='py-2 pr-3 text-right tabular-nums text-foreground'>{formatPrice(quote.last)}</td>
                            <td className='py-2 pr-3 text-right'><ChangeText value={quote.changePct} /></td>
                            <td className='py-2 pr-3 text-right tabular-nums'>{formatKes(cap)}</td>
                            <td className='py-2 pr-3 text-right tabular-nums'>{peer.eps && peer.eps > 0 ? formatMultiple(quote.last / peer.eps) : 'n/m'}</td>
                            <td className='py-2 pr-3 text-right tabular-nums'>{peer.dps ? formatPercent((peer.dps / quote.last) * 100, 2, false) : '-'}</td>
                            <td className='py-2 text-right'><ChangeText value={peer.perf.y1} decimals={1} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default PeersTable
